/**
 * NeedsReviewPopup — what is waiting for you, after sign-in
 * =========================================================
 * Brian, 27 October 2026, asked for a pop-up rather than finding SHF Holdings'
 * unposted sales on a tab (D-278). It opens after sign-in, again each day while
 * anything is still waiting, and at once when something new joins the list
 * (`needsReview.shouldOpen`). Every row names the company and the date and
 * links to the page where it is dealt with.
 *
 * It is in the shell, so it shows on whatever page the reader lands on. It
 * reads the list once per load; a failed read shows nothing rather than an
 * error box over the page, because the Dashboard's strip reads the same list
 * and says when it could not.
 *
 * Brian, 27 October 2026: "broker trades should just post, only conversions
 * and other items should be flagged and required for approval" (D-281). Clean
 * broker rows post by themselves, so a broker row here is a decision, and it
 * is answered here: Post (over the sweep's hold, with a reason) or Rule out.
 * Each shows what it would do before the button that does it (§1). A sale
 * waiting on a conversion is listed under the conversion, and posts when the
 * conversion is confirmed.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { day } from "./format";
import { dealReportPath } from "./links";
import BrokerRowDecision from "./BrokerRowDecision";
import {
  NEEDS_REVIEW_CHANGED,
  NEEDS_REVIEW_URL,
  NO_COMPANY,
  groupByCompany,
  readDismissed,
  saveDismissed,
  shouldOpen,
  summaryLine,
} from "./needsReview";
import "./NeedsReviewPopup.css";

/**
 * `onData` hands each read of the list to the shell, which counts it on the
 * Approvals link, so the list is read once a load and not twice. A
 * `needs-review-changed` event (Approvals fires it after every answer) reads it
 * again, so the count comes down without a reload; that re-read never opens
 * the pop-up.
 */
export default function NeedsReviewPopup({ onData }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [settled, setSettled] = useState({});
  const closeRef = useRef(null);
  const settle = useCallback((key, words) => {
    setSettled((s) => ({ ...s, [key]: words }));
    window.dispatchEvent(new Event(NEEDS_REVIEW_CHANGED));
  }, []);

  useEffect(() => {
    let live = true;
    const read = (first) => api
      .get(NEEDS_REVIEW_URL)
      .then((res) => {
        if (!live) return;
        setData(res.data);
        if (onData) onData(res.data);
        if (first) setOpen(shouldOpen(res.data, readDismissed()));
      })
      .catch(() => {});
    read(true);
    const again = () => read(false);
    window.addEventListener(NEEDS_REVIEW_CHANGED, again);
    return () => {
      live = false;
      window.removeEventListener(NEEDS_REVIEW_CHANGED, again);
    };
  }, [onData]);

  const dismiss = useCallback(() => {
    saveDismissed(data);
    setOpen(false);
  }, [data]);

  useEffect(() => {
    if (!open) return undefined;
    if (closeRef.current) closeRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dismiss]);

  if (!open || !data) return null;

  const groups = groupByCompany(data.items);
  const email = data.email || {};
  const summary = summaryLine(data.counts);

  return (
    <div className="nr-overlay" onClick={(e) => e.target === e.currentTarget && dismiss()}>
      <div className="nr-dialog" role="dialog" aria-modal="true" aria-labelledby="nr-title">
        <header className="nr-head">
          <div>
            <h2 id="nr-title">Waiting for your review</h2>
            {summary && <p className="nr-summary">{summary}</p>}
          </div>
          <button type="button" className="nr-x" onClick={dismiss} aria-label="Close">✕</button>
        </header>

        {email.reads_as && (
          <p className="nr-email" role="status">
            <strong>The morning email is not reaching you.</strong> {email.reads_as}
          </p>
        )}

        <div className="nr-body">
          {groups.map((group) => (
            <section key={group.company} className="nr-group">
              <h3>
                {group.dealId !== null && group.company !== NO_COMPANY ? (
                  <Link to={dealReportPath(group.dealId)} onClick={dismiss}>{group.company}</Link>
                ) : (
                  group.company
                )}
              </h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item.key}>
                    <span className="nr-date">{item.date ? day(item.date) : "No date"}</span>
                    <span className="nr-what">
                      {item.what}
                      <small>{item.why}</small>
                      {item.then?.length > 0 && (
                        <small className="nr-then">
                          Confirming it posts {item.then.map((t) => `${t.what} (${day(t.date)})`).join("; ")}.
                        </small>
                      )}
                      {item.kind === "broker_row" && item.row_id != null && (
                        settled[item.key]
                          ? <strong className="nr-settled" role="status">{settled[item.key]}</strong>
                          : <BrokerRowDecision item={item} onDone={settle} />
                      )}
                    </span>
                    <Link className="nr-open" to={item.where} onClick={dismiss}>Open</Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {!groups.length && <p className="nr-empty">Nothing else is waiting.</p>}
        </div>

        <footer className="nr-foot">
          {data.bank_rows_waiting > 0 ? (
            <Link to="/bank-feed" onClick={dismiss} className="nr-bank">
              {data.bank_rows_waiting} bank {data.bank_rows_waiting === 1 ? "row" : "rows"} waiting to post on Bank Feed
            </Link>
          ) : <span />}
          <span className="nr-foot-actions">
            <button type="button" ref={closeRef} className="nr-later" onClick={dismiss}>
              Later
            </button>
            <Link to="/approvals" className="nr-done" onClick={dismiss}>
              Go to Approvals
            </Link>
          </span>
        </footer>
      </div>
    </div>
  );
}
